import React, { useState, useRef } from 'react'
import { Typography, Chip, TextField, Collapse } from '@material-ui/core'
import { ExpandMore, ExpandLess } from '@material-ui/icons'
import axios from 'axios'
import AreaBreakdownTable from './AreaBreakdownTable'
import ComponentRecipeSummary from './ComponentRecipeSummary'

const fmt = (v) => '$' + (v || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })

const ServiceDetail = ({ projectService, project, url, onUpdate }) => {
  const [areasExpanded, setAreasExpanded] = useState(false)
  const debounceRef = useRef(null)

  const hasAreas = (projectService.areas || []).length > 0

  // Direct quantity/unit editing — works without areas
  // Auto-triggers recalculate when quantity changes so costs update immediately
  const handleFieldChange = (field, value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        await axios.put(`${url}/api/bid-estimation/project-services/${projectService._id}`, { [field]: value })
        if (field === 'totalQuantity') {
          await axios.post(`${url}/api/bid-estimation/project-services/${projectService._id}/recalculate`)
        }
        onUpdate()
      } catch (e) {
        console.error('Service update failed:', e)
      }
    }, 600)
  }

  return (
    <div>
      {/* Service header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {projectService.serviceCategory}
          </div>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            {projectService.serviceName}
          </Typography>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Direct qty/unit input — always visible */}
          <TextField
            size="small" variant="outlined" type="number" placeholder="Qty"
            defaultValue={projectService.totalQuantity || ''}
            onBlur={(e) => {
              const val = Number(e.target.value)
              if (val !== projectService.totalQuantity) {
                handleFieldChange('totalQuantity', val)
              }
            }}
            InputProps={{ style: { fontSize: 13, width: 80 } }}
          />
          <TextField
            size="small" variant="outlined" placeholder="Unit"
            defaultValue={projectService.unit || ''}
            onBlur={(e) => {
              if (e.target.value !== projectService.unit) {
                handleFieldChange('unit', e.target.value)
              }
            }}
            InputProps={{ style: { fontSize: 13, width: 60 } }}
          />
          <Chip size="small" label={`Cost: ${fmt(projectService.totalCost)}`}
            style={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} />
          <Chip size="small" label={`Sell: ${fmt(projectService.totalSell)}`}
            style={{ backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
        </div>
      </div>

      {/* Collapsible area breakdown — optional */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0', marginBottom: 20 }}>
        <div
          onClick={() => setAreasExpanded(!areasExpanded)}
          style={{
            padding: '8px 16px', display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', cursor: 'pointer', userSelect: 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {areasExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              Area Breakdown
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {hasAreas ? `${projectService.areas.length} area${projectService.areas.length !== 1 ? 's' : ''}` : 'Optional — click to add areas'}
            </Typography>
          </div>
        </div>
        <Collapse in={areasExpanded}>
          <AreaBreakdownTable projectService={projectService} url={url} onUpdate={onUpdate} />
        </Collapse>
      </div>

      <ComponentRecipeSummary projectService={projectService} project={project} url={url} onUpdate={onUpdate} />
    </div>
  )
}

export default ServiceDetail
